import React, { useRef } from 'react';

import { withEventScope } from '@repo/event-processor';
import { Button, secondaryTheme } from '@repo/ui/components/Buttons';
import LoadingIcon from '@repo/ui/components/Icon/LoadingIcon';
import RotateIcon from '@repo/ui/components/Icon/RotateIcon';
import UploadIcon from '@repo/ui/components/Icon/UploadIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { themeStyle } from '@repo/ui/theming';

import {
  ActionWrapper,
  Card,
  CenteredSpinnerWrapper,
  Wrapper,
} from 'components/AiTools/ImageEditor/styles';
import { ImageEditorProps } from 'components/AiTools/ImageEditor/types';
import { rasterImageTypes, hasValidDimensions } from 'global/utils/fileUtils';
import { FlexRow } from 'styles/layouts';

const ImageEditor: React.FC<ImageEditorProps> = (props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = async (
    event
  ): Promise<void> => {
    const file = event.target.files?.[0];

    if (props.onReplace && file && (await hasValidDimensions(file))) {
      props.onReplace(file);
    }
  };

  return (
    <Card>
      <Wrapper background={props.background}>
        {props.children}
        {props.isLoading && (
          <CenteredSpinnerWrapper
            theme={{ spinnerColor: themeStyle.varBrandMedium }}
          >
            <LoadingIcon height="40px" />
          </CenteredSpinnerWrapper>
        )}
      </Wrapper>
      <Spacer height="24px" />
      <ActionWrapper
        justifyContent={props.additionActions ? 'space-between' : 'center'}
        multiline={!!props.multiline}
      >
        <FlexRow gap="8px">
          {props.onReplace ? (
            <Button
              ariaLabel="Upload Image"
              disabled={props.isLoading}
              height={!props.small ? '40px' : '30px'}
              icon={{ component: UploadIcon }}
              label={!props.small ? 'Upload Image' : ''}
              name="uploadImageButton"
              theme={secondaryTheme}
              width={!props.small ? '156px' : '30px'}
              onClick={handleClick}
            />
          ) : null}
          {props.onReset ? (
            <Button
              disabled={props.isLoading || !props.isModified}
              height={!props.small ? '40px' : '30px'}
              icon={{ component: RotateIcon }}
              label={!props.small ? 'Reset' : ''}
              name="resetImageEditorButton"
              theme={secondaryTheme}
              width={!props.small ? '97px' : '30px'}
              onClick={props.onReset}
            />
          ) : null}
        </FlexRow>
        {props.additionActions}
      </ActionWrapper>
      <input
        ref={fileInputRef}
        hidden
        accept={rasterImageTypes.join(',')}
        type="file"
        onChange={handleChange}
        onClick={(event): void => {
          (event.target as HTMLInputElement).value = '';
        }}
      />
    </Card>
  );
};

export default withEventScope(ImageEditor, { module: 'imageEditor' });
